import { isAPIError } from "@/core/services";
import { readBusinessError } from "@/core/utils";
import { useAsyncQuery, useTableState } from "@/ds";
import {
  DEFAULT_FICHAS_FINANCEIRAS_SORT,
  getFichaFinanceira,
  getPreviaFichaFinanceira,
  isFichasFinanceirasSortField,
  listFichasFinanceiras,
} from "@/modules/financeiro-fichas";
import type {
  FichaFinanceiraDetalhe,
  FichaPrevia,
  FichasFinanceirasPage,
} from "@/modules/financeiro-fichas";

/** MS003. */
export const FICHAS_ERROR_MESSAGE = "Não foi possível carregar as fichas. Tente novamente.";
export const FICHAS_FORBIDDEN_MESSAGE = "Você não tem permissão para consultar as fichas da obra.";
export const FICHA_ERROR_MESSAGE = "Não foi possível carregar a ficha. Tente novamente.";
export const PREVIA_ERROR_MESSAGE = "Não foi possível carregar a prévia da ficha. Tente novamente.";
export const PREVIA_FORBIDDEN_MESSAGE = "Você não tem permissão para gerar fichas da obra.";

export type FichasQueryStatus = "loading" | "success" | "error" | "forbidden";

interface Query<T> {
  status: FichasQueryStatus;
  data: T | null;
  errorMessage: string | null;
  /** Mensagem do 422 de regra de negócio (ex.: MS006); `null` nas demais falhas. */
  businessMessage: string | null;
  reload: () => void;
}

/** 422 de regra de negócio (ex.: MS006); `null` nas demais falhas. */
function readBusinessMessage(error: unknown): string | null {
  return isAPIError(error) && error.status === 422
    ? (readBusinessError(error)?.message ?? null)
    : null;
}

/** Consulta única com cancelamento, recarga e 403 como "sem permissão". */
export function useFichasQuery<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  fallbackMessage: string,
  key: string,
  { enabled = true, forbiddenMessage = FICHAS_FORBIDDEN_MESSAGE, keepPrevious = false } = {},
): Query<T> {
  const { error, ...query } = useAsyncQuery<T | null>({
    fetcher,
    queryKey: key,
    initialData: null,
    enabled,
    keepPrevious,
    forbiddenMessage,
    errorMessage: fallbackMessage,
    resolveErrorMessage: readBusinessMessage,
  });

  return { ...query, businessMessage: readBusinessMessage(error) };
}

/**
 * Fichas da obra (HU 2679): o servidor pagina e ordena. Página, linhas por página e ordenação
 * ficam aqui; mudar qualquer uma consulta de novo, e as linhas atuais seguem na tela até a resposta.
 */
export function useFichasFinanceiras(obraId: string, enabled = true) {
  const table = useTableState({
    isSortField: isFichasFinanceirasSortField,
  });
  const { sort, page, rows } = table;
  const sortKey = sort ? `${sort.field}:${sort.direction}` : "";

  const query = useFichasQuery<FichasFinanceirasPage>(
    (signal) =>
      listFichasFinanceiras(obraId, {
        page: page + 1,
        pageSize: rows,
        sort: sort ?? DEFAULT_FICHAS_FINANCEIRAS_SORT,
        signal,
      }),
    FICHAS_ERROR_MESSAGE,
    `${obraId}|${page}|${rows}|${sortKey}`,
    { enabled, keepPrevious: true },
  );

  return { ...query, items: query.data?.items ?? [], total: query.data?.total ?? 0, table };
}

/** Ficha selecionada (visão somente leitura). */
export function useFichaFinanceira(obraId: string, fichaId: string, enabled = true) {
  return useFichasQuery<FichaFinanceiraDetalhe>(
    (signal) => getFichaFinanceira(obraId, fichaId, signal),
    FICHA_ERROR_MESSAGE,
    `${obraId}/${fichaId}`,
    { enabled },
  );
}

/** Prévia da ficha a gerar (HU 2684); só consulta com `enabled`. */
export function usePreviaFichaFinanceira(obraId: string, enabled = true) {
  return useFichasQuery<FichaPrevia>(
    (signal) => getPreviaFichaFinanceira(obraId, signal),
    PREVIA_ERROR_MESSAGE,
    `previa/${obraId}`,
    { enabled, forbiddenMessage: PREVIA_FORBIDDEN_MESSAGE },
  );
}
